/**
 * WebEDI Errors Hooks
 *
 * This file contains React hooks for working with WebEDI errors.
 */

import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { ui } from '@procuros/datachecks';

import { logError } from 'services/logging/logging';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { GenericError } from 'support/helpers/errors/errors';
import { DocumentTypeWithVirtual, PartnerDTO } from 'support/types';
import { DisplayModes } from 'components/DataTable/Types';

import { WebEDIErrorsServiceSingleton } from '.';
import { formatHiddenError } from './logging';

/**
 * Hook for managing document-level errors with UI configuration.
 * Organizes errors according to the UI configuration and logs hidden errors.
 */
export const useDocumentErrors = (
  uiConfig: Array<ui.Section> | undefined,
  displayMode: DisplayModes,
  variables: Record<string, string>,
  documentType: DocumentTypeWithVirtual | undefined,
  senderPartner: Pick<PartnerDTO, 'name' | 'id'> | null | undefined,
  receiverPartner: Pick<PartnerDTO, 'name' | 'id'> | null | undefined,
) => {
  const {
    getValues,
    formState: { errors, dirtyFields },
  } = useFormContext();
  const isBigMessage = useWebEDIStore((state) => state.isBigMessage);

  useEffect(() => {
    const hiddenErrors = WebEDIErrorsServiceSingleton.processDocumentErrors(
      errors,
      variables,
      uiConfig,
      displayMode,
      dirtyFields,
      isBigMessage,
    );

    // Log all hidden errors with full context if we have all required data
    if (hiddenErrors.length > 0 && documentType && senderPartner && receiverPartner) {
      try {
        hiddenErrors.forEach((error) => {
          const formattedError = formatHiddenError({
            error,
            documentType,
            senderPartner,
            receiverPartner,
            flattenData: getValues(),
          });

          logError(new GenericError(`[CS] ${formattedError.message}`, JSON.stringify(formattedError, null, 2)));
        });
      } catch (err) {
        logError(err);
      }
    }
  }, [
    displayMode,
    errors,
    getValues,
    uiConfig,
    variables,
    documentType,
    senderPartner,
    receiverPartner,
    dirtyFields,
    isBigMessage,
  ]);
};

/**
 * Hook for focusing on an error when a component mounts, typically used
 * after navigation to a page with errors.
 */
export const useFocusErrorOnMount = () => {
  const location: { state?: { focusOnField?: { id: string } } } = useLocation();
  const { focusOnField } = location.state || {};

  useEffect(() => {
    if (focusOnField?.id) {
      const error = WebEDIErrorsServiceSingleton.getErrorById(focusOnField.id);
      if (error) {
        WebEDIErrorsServiceSingleton.setCurrentError(error);
      }
    }
  }, [focusOnField]);
};
